import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

// Helper to get the correct edge function URL.
//
// IMPORTANT: we base the decision on VITE_SUPABASE_URL, NOT
// window.location.hostname. Running the Vite dev server on localhost does NOT
// mean Supabase is local — in this project we always point at the production
// Supabase (apegsjpotzvxumdedwty) from .env.local. The old check
// (`hostname === 'localhost'`) incorrectly routed every dev request to
// http://127.0.0.1:54321 and surfaced "Failed to connect to ..." errors for
// users who don't run the Supabase CLI.
export const getEdgeFunctionUrl = (functionName: string): string => {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL ?? '').toString();
  const supabaseIsLocal =
    supabaseUrl.includes('127.0.0.1') ||
    supabaseUrl.includes('localhost') ||
    supabaseUrl.includes('kong');

  if (supabaseIsLocal) {
    const ngrokTunnel = import.meta.env.VITE_NGROK_TUNNEL;
    if (ngrokTunnel) {
      return `${ngrokTunnel}/functions/v1/${functionName}`;
    }
    return `http://127.0.0.1:54321/functions/v1/${functionName}`;
  }

  // Derive the functions host from VITE_SUPABASE_URL directly — avoids the
  // "which project ref?" fallback and always matches whatever project the
  // frontend auth client is pointing at.
  try {
    const u = new URL(supabaseUrl || 'https://apegsjpotzvxumdedwty.supabase.co');
    return `${u.protocol}//${u.host}/functions/v1/${functionName}`;
  } catch {
    const projectRef = import.meta.env.VITE_SUPABASE_PROJECT_REF || 'apegsjpotzvxumdedwty';
    return `https://${projectRef}.supabase.co/functions/v1/${functionName}`;
  }
};

// Single source of truth for the local-vs-remote decision used below.
function supabaseIsLocal(): boolean {
  const url = (import.meta.env.VITE_SUPABASE_URL ?? '').toString();
  return url.includes('127.0.0.1') || url.includes('localhost') || url.includes('kong');
}

// getSession() returns whatever session is stored, INCLUDING one whose access
// token already expired (a slept laptop / long-idle tab misses the auto-refresh
// timer). Sending that token yields a gateway 401 with no runtime log — seen
// live on generate-oauth-state 2026-07-20, where it surfaced as a dead
// "Auto-Recovery" button. Refresh whenever the token is missing or within a
// minute of expiry, and only then hand it out.
async function getFreshAccessToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const expiresAtMs = (session?.expires_at ?? 0) * 1000;
  if (session?.access_token && expiresAtMs > Date.now() + 60_000) {
    return session.access_token;
  }

  const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
  if (refreshError || !refreshData?.session?.access_token) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  return refreshData.session.access_token;
}

// Helper to invoke edge functions with proper error handling
export const invokeEdgeFunction = async <T = any>(
  functionName: string,
  options?: {
    body?: Record<string, any>;
    headers?: Record<string, string>;
  },
): Promise<T> => {
  try {
    const accessToken = await getFreshAccessToken();

    // For local development, use direct fetch instead of supabase.functions.invoke
    if (supabaseIsLocal()) {
      const url = getEdgeFunctionUrl(functionName);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          Origin: window.location.origin,
          ...options?.headers,
        },
        body: JSON.stringify(options?.body || {}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Edge function error: ${errorText}`);
      }

      return await response.json();
    }

    // For production, use supabase.functions.invoke
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: options?.body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...options?.headers,
      },
    });

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error(`Error invoking edge function ${functionName}:`, error);
    throw error;
  }
};
